import { resolveStoredFileUrl } from "utils/storedFileUrl";
// React
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Card, Fade, Icon, Modal } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DataTableCustom from "components/Tables/DataTableCustom";
import CustomPagination from "components/Pagination";
import { formatDateTimeARG, formatMoneyPunto } from "utils/formatters";
import pdfIcon from "assets/images/file-type-pdf2.svg";
import { jsPDF } from "jspdf";
import FileHistorial from 'layouts/PDF/clientes/cargaSaldos';

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: "80%",
  overflow: "hidden",
};

const QUERY_HISTORIAL = gql`
  query historial($pagination: PaginationInput, $order: [Order]) {
    paginationInfo { pages total }
    historialCargaSaldos(pagination: $pagination, order: $order) {
      fechaPago
      tipoCarga
      comprobanteFile
      totalPago
      pagos {
        id
        idCliente
        cliente { id nombre apellido dni }
      }
    }
  }
`;

export default function HistorialModal({ open, handleClose, idSede }) {
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [load, { data, loading }] = useLazyQuery(QUERY_HISTORIAL, {
    fetchPolicy: "no-cache",
  });
  const [pdfData, setPdfData] = useState(null);

  const handleGeneratePdf = (row) => {
    setPdfData(row);
  };

  const handlePdfGenerated = () => {
    setPdfData(null);
  };

  useEffect(() => {
    if (open) {
      load({
        variables: {
          pagination: { page, limit: 50 },
          order: [{ field: "fechaPago", order: "DESC" }],
        },
        onCompleted: (res) => setPagesInfo(res?.paginationInfo || { pages: 0, total: 0 }),
      });
    }
  }, [open, page, idSede, load]);

  const rows = data?.historialCargaSaldos || [];

  const columns = useMemo(() => [
    {
      field: "fechaPago",
      headerName: "Fecha",
      align: "center",
      width: "16%",
      renderCell: (row) => (
        <SoftTypography variant="button" color="secondary">
          {formatDateTimeARG(row?.fechaPago)}
        </SoftTypography>
      ),
      customSortValue: (row) => {
        const d = row?.fechaPago ? new Date(row.fechaPago) : null;
        return d ? d.getTime() : 0;
      },
    },
    {
      field: "tipoCarga",
      headerName: "Tipo de carga",
      align: "center",
      width: "18%",
      renderCell: (row) => (
        <SoftTypography variant="button" color="dark">
          {row?.tipoCarga || "-"}
        </SoftTypography>
      ),
    },
    {
      field: "comprobante",
      headerName: "Comprobante",
      align: "center",
      sortable: false,
      width: "13%",
      renderCell: (row) => (
        <SoftBox display="flex" justifyContent="center" alignItems="center">
          <SoftButton
            variant="text"
            color="dark"
            size="medium"
            iconOnly
            circular
            onClick={(e) => {
              e.stopPropagation();
              if (row?.comprobanteFile) {
                const url = resolveStoredFileUrl(row.comprobanteFile);
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
            title="Ver comprobante"
            sx={{
              minWidth: 'auto',
              width: '48px',
              height: '48px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SoftBox
              component="img"
              src={pdfIcon}
              alt="Ver comprobante"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </SoftButton>
        </SoftBox>
      ),
    },
    {
      field: "monto",
      headerName: "Monto",
      align: "center",
      width: "14%",
      renderCell: (row) => (
        <SoftTypography variant="button" color="dark">
          {`$${formatMoneyPunto(row?.totalPago || 0)}`}
        </SoftTypography>
      ),
      customSortValue: (row) => row?.totalPago || 0,
    },
    {
      field: "cantidad",
      headerName: "Cantidad",
      align: "center",
      width: "9%",
      renderCell: (row) => (
        <SoftTypography variant="button" color="dark">
          {Array.isArray(row?.pagos) ? row.pagos.length : 0}
        </SoftTypography>
      ),
      customSortValue: (row) => (Array.isArray(row?.pagos) ? row.pagos.length : 0),
    },
    {
      field: "montoTotal",
      headerName: "Monto total",
      align: "center",
      width: "20%",
      renderCell: (row) => {
        const cantidad = Array.isArray(row?.pagos) ? row.pagos.length : 0;
        const total = (row?.totalPago || 0) * cantidad;
        return (
          <SoftTypography variant="button" color="dark">
            {`$${formatMoneyPunto(total)}`}
          </SoftTypography>
        );
      },
      customSortValue: (row) => {
        const cantidad = Array.isArray(row?.pagos) ? row.pagos.length : 0;
        return (row?.totalPago || 0) * cantidad;
      },
    },
    {
      field: "pdf",
      headerName: "PDF",
      align: "center",
      sortable: false,
      width: "8%",
      renderCell: (row) => (
        <SoftBox display="flex" justifyContent="center" alignItems="center">
          <SoftButton
            variant="text"
            color="dark"
            size="medium"
            iconOnly
            circular
            onClick={(e) => { e.stopPropagation(); handleGeneratePdf(row); }}
            title="Descargar resumen"
            sx={{
              minWidth: 'auto',
              width: '40px',
              height: '40px',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SoftBox
              component="img"
              src={pdfIcon}
              alt="Ver comprobante"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </SoftButton>
        </SoftBox>
      ),
    },
  ], []);

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h5" fontWeight="medium">Historial de cargas</SoftTypography>
            <SoftButton color="dark" circular onClick={handleClose}>Cerrar</SoftButton>
          </SoftBox>

          <SoftBox px={3} pb={2}>
            <DataTableCustom
              columns={columns}
              zebraStripes={false}
              headerStyles={{
                backgroundColor: "#ffffff",             // fondo blanco
                borderBottom: "1.5px solid #070606ff",
                py: 1.1,                               // menos alto
                "& .MuiTypography-root": {
                  fontWeight: 600,                      // negrita media
                  color: "#344767",                     // gris azulado
                  // textTransform: "none",                // sin uppercase
                  fontSize: "0.7rem",
                  letterSpacing: 0.2,
                },
              }}
              data={rows}
              loading={loading}
              showActions={false}
              defaultSort={{ field: "fechaPago", direction: "desc" }}
              // Filas colapsables con el detalle de clientes
              rowExpandable={(row) => Array.isArray(row?.pagos) && row.pagos.length > 0}
              expandedRowRender={(row) => (
                <SoftBox p={0}>
                  <SoftBox display="flex" width="100%" py={1} px={2} sx={{ backgroundColor: "rgba(125, 141, 140, 0.19)" }}>
                    <SoftBox width="15%" pl={1}><SoftTypography variant="caption" fontWeight="bold">ID</SoftTypography></SoftBox>
                    <SoftBox width="30%"><SoftTypography variant="caption" fontWeight="bold">Nombre</SoftTypography></SoftBox>
                    <SoftBox width="30%"><SoftTypography variant="caption" fontWeight="bold">Apellido</SoftTypography></SoftBox>
                    <SoftBox width="25%"><SoftTypography variant="caption" fontWeight="bold">DNI</SoftTypography></SoftBox>
                  </SoftBox>
                  {row?.pagos?.map((p) => (
                    <SoftBox key={p.id} display="flex" py={1} px={1} sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                      <SoftBox width="15%" pl={1}><SoftTypography variant="button">{p?.cliente?.id ?? p?.idCliente ?? "-"}</SoftTypography></SoftBox>
                      <SoftBox width="30%"><SoftTypography variant="button">{p?.cliente?.nombre ?? "-"}</SoftTypography></SoftBox>
                      <SoftBox width="30%"><SoftTypography variant="button">{p?.cliente?.apellido ?? "-"}</SoftTypography></SoftBox>
                      <SoftBox width="25%"><SoftTypography variant="button">{p?.cliente?.dni ?? "-"}</SoftTypography></SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              )}
            />
          </SoftBox>

          <SoftBox px={3} pb={3}>
            <CustomPagination
              loading={loading}
              length={rows?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </SoftBox>

          {pdfData && (
            <SoftBox sx={{ display: 'none' }}>
              <FileHistorial
                data={pdfData}
                onGeneratePdf={handlePdfGenerated}
              />
            </SoftBox>
          )}
        </Card>
      </Fade>
    </Modal>
  );
}

HistorialModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idSede: PropTypes.number,
};
